import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'

const getSuitColor = (suit: string) => suit === 'hearts' || suit === 'diamonds' ? 'text-red-500' : 'text-slate-800'
const getSuitIcon = (suit: string) => {
  switch (suit) {
    case 'spades': return '♠'
    case 'hearts': return '♥'
    case 'clubs': return '♣'
    case 'diamonds': return '♦'
    default: return ''
  }
}

interface Card {
  rank: string
  suit: string
}

interface HeroCardsProps {
  cards: Card[]
  isCompact: boolean
  visible: boolean
  handStrength?: string
  winAmount?: number | null
}

export function HeroCards({ cards, isCompact, visible, handStrength, winAmount }: HeroCardsProps) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.9 }}
          transition={{ type: 'spring', damping: 20, stiffness: 200 }}
          className="flex flex-col items-center gap-1"
        >
          <div className="flex">
            {cards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ rotateY: 180 }}
                animate={{ rotateY: 0 }}
                transition={{ delay: i * 0.15, duration: 0.4, type: 'spring' }}
                className={cn(
                  'relative select-none',
                  isCompact ? 'w-[52px] h-[72px]' : 'w-[68px] h-[94px]',
                  i === 0 ? '-rotate-6' : 'rotate-6 -ml-4'
                )}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div className={cn('absolute inset-0 bg-black/30 rounded-lg blur-md', isCompact ? 'translate-y-1' : 'translate-y-2')} />
                <div className="relative w-full h-full bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-[5px] border border-gray-200/80 overflow-hidden shadow-lg">
                  <div className={cn('absolute top-0.5 left-1 flex flex-col items-center leading-none', getSuitColor(card.suit))}>
                    <span className={cn('font-black font-serif', isCompact ? 'text-xl' : 'text-3xl')}>{card.rank}</span>
                    <span className={cn('-mt-0.5', isCompact ? 'text-sm' : 'text-xl')}>{getSuitIcon(card.suit)}</span>
                  </div>
                  <div className={cn('absolute bottom-1.5 right-2 rotate-180', getSuitColor(card.suit))}>
                    <span className={cn(isCompact ? 'text-xl' : 'text-3xl')}>{getSuitIcon(card.suit)}</span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Hand strength label */}
          {handStrength && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="px-2 py-0.5 rounded bg-black/50 backdrop-blur-sm"
            >
              <span className="text-[9px] font-bold text-gold/80 uppercase tracking-wider">{handStrength}</span>
            </motion.div>
          )}

          {/* Inline win badge */}
          <AnimatePresence>
            {winAmount != null && (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                className="px-3 py-1 rounded-full bg-gold/20 border border-gold/40"
              >
                <span className="text-sm font-mono font-bold text-gold">+${winAmount.toLocaleString()}</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
